import type { PluginManifestEntry } from './types';

export type ManifestSource = 'dotnet' | 'python';
export type ManifestSaveTarget = ManifestSource | 'both';
export type ManifestKind = 'marketplace' | 'installed';

const DOTNET_MARKETPLACE_URL = '/api/dotnet/plugins/marketplace/manifest';
const DOTNET_INSTALLED_URL = '/api/dotnet/plugins/installed/manifest';
const PYTHON_MARKETPLACE_URL = '/api/python/plugins/marketplace/manifest';
const PYTHON_INSTALLED_URL = '/api/python/plugins/installed/manifest';
const DOTNET_MARKETPLACE_SCHEMA_URL = '/api/dotnet/plugins/marketplace/manifest/schema';
const DOTNET_INSTALLED_SCHEMA_URL = '/api/dotnet/plugins/installed/manifest/schema';
const PYTHON_MARKETPLACE_SCHEMA_URL = '/api/python/plugins/marketplace/manifest/schema';
const PYTHON_INSTALLED_SCHEMA_URL = '/api/python/plugins/installed/manifest/schema';

let pythonManifestAvailable = true;

function isPythonUrl(url: string) {
  return url.startsWith('/api/python/');
}

export interface ManifestLoadResult {
  merged: PluginManifestEntry[];
  sourcesById: Record<string, ManifestSource[]>;
  bySource: Record<ManifestSource, PluginManifestEntry[]>;
  errors: ManifestSource[];
}

interface LoadOptions {
  signal?: AbortSignal;
  getAccessToken?: () => Promise<string | null>;
  includePython?: boolean;
}

async function fetchJson(url: string, token?: string | null, signal?: AbortSignal) {
  if (isPythonUrl(url) && !pythonManifestAvailable) {
    return null;
  }
  const headers: HeadersInit = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(url, {
    headers,
    signal,
  });
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      if (isPythonUrl(url)) {
        pythonManifestAvailable = false;
      }
      return null;
    }
    if (response.status === 404) {
      return null;
    }
    throw new Error(`Failed to fetch ${url}`);
  }
  return response.json();
}

async function fetchManifest(url: string, token?: string | null, signal?: AbortSignal) {
  const data = await fetchJson(url, token, signal);
  if (!data) {
    return [];
  }
  return Array.isArray(data) ? (data as PluginManifestEntry[]) : [];
}

function isEmptyValue(value: unknown) {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

function mergeEntries(primary: PluginManifestEntry, secondary: PluginManifestEntry) {
  const merged: PluginManifestEntry = { ...secondary, ...primary };
  const primaryRecord = primary as unknown as Record<string, unknown>;
  const secondaryRecord = secondary as unknown as Record<string, unknown>;
  const mergedRecord = merged as unknown as Record<string, unknown>;

  Object.keys(primary).forEach((key) => {
    const value = primaryRecord[key];
    if (isEmptyValue(value)) {
      const fallback = secondaryRecord[key];
      if (!isEmptyValue(fallback)) {
        mergedRecord[key] = fallback;
      }
    }
  });
  return merged;
}

function getManifestUrl(kind: ManifestKind, source: ManifestSource) {
  if (kind === 'installed') {
    return source === 'dotnet' ? DOTNET_INSTALLED_URL : PYTHON_INSTALLED_URL;
  }
  return source === 'dotnet' ? DOTNET_MARKETPLACE_URL : PYTHON_MARKETPLACE_URL;
}

function getSchemaUrl(kind: ManifestKind, source: ManifestSource) {
  if (kind === 'installed') {
    return source === 'dotnet' ? DOTNET_INSTALLED_SCHEMA_URL : PYTHON_INSTALLED_SCHEMA_URL;
  }
  return source === 'dotnet' ? DOTNET_MARKETPLACE_SCHEMA_URL : PYTHON_MARKETPLACE_SCHEMA_URL;
}

export async function loadManifestSources(
  kind: ManifestKind,
  { signal, getAccessToken, includePython = false }: LoadOptions = {}
): Promise<ManifestLoadResult> {
  const errors: ManifestSource[] = [];
  const token = getAccessToken ? await getAccessToken().catch(() => null) : null;

  const pythonPromise = includePython && token && pythonManifestAvailable
    ? fetchManifest(getManifestUrl(kind, 'python'), token, signal)
    : Promise.resolve([] as PluginManifestEntry[]);

  const [dotnetResult, pythonResult] = await Promise.allSettled([
    fetchManifest(getManifestUrl(kind, 'dotnet'), token, signal),
    pythonPromise,
  ]);

  const dotnet = dotnetResult.status === 'fulfilled' ? dotnetResult.value : [];
  const python = pythonResult.status === 'fulfilled' ? pythonResult.value : [];

  if (dotnetResult.status === 'rejected') {
    errors.push('dotnet');
  }
  if (pythonResult.status === 'rejected') {
    errors.push('python');
  }

  const sourcesById: Record<string, ManifestSource[]> = {};
  const mergedById = new Map<string, PluginManifestEntry>();

  const addEntries = (entries: PluginManifestEntry[], source: ManifestSource) => {
    entries.forEach((entry) => {
      if (!entry || !entry.id) {
        return;
      }
      const id = entry.id;
      if (!sourcesById[id]) {
        sourcesById[id] = [];
      }
      if (!sourcesById[id].includes(source)) {
        sourcesById[id].push(source);
      }
      if (!mergedById.has(id)) {
        mergedById.set(id, entry);
      } else {
        const existing = mergedById.get(id) as PluginManifestEntry;
        const merged = source === 'dotnet'
          ? mergeEntries(entry, existing)
          : mergeEntries(existing, entry);
        mergedById.set(id, merged);
      }
    });
  };

  addEntries(dotnet, 'dotnet');
  addEntries(python, 'python');

  return {
    merged: Array.from(mergedById.values()),
    sourcesById,
    bySource: {
      dotnet,
      python,
    },
    errors,
  };
}

export async function loadManifestSchema(
  kind: ManifestKind,
  { signal, getAccessToken }: LoadOptions = {}
) {
  const token = getAccessToken ? await getAccessToken().catch(() => null) : null;
  try {
    const schema = await fetchJson(getSchemaUrl(kind, 'dotnet'), token, signal);
    return schema && typeof schema === 'object' ? (schema as Record<string, unknown>) : null;
  } catch {
    if (!token) {
      return null;
    }
    try {
      const schema = await fetchJson(getSchemaUrl(kind, 'python'), token, signal);
      return schema && typeof schema === 'object' ? (schema as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
}

export async function saveManifest(
  kind: ManifestKind,
  source: ManifestSource,
  manifest: PluginManifestEntry[],
  token?: string | null
) {
  const url = getManifestUrl(kind, source);
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(manifest),
  });

  if (!response.ok) {
    throw new Error(`Failed to save ${source} manifest.`);
  }

  const data = await response.json();
  return Array.isArray(data) ? (data as PluginManifestEntry[]) : manifest;
}

export function splitManifestBySource(
  entries: PluginManifestEntry[],
  sourcesById: Record<string, ManifestSource[]>,
  fallback: ManifestSaveTarget
) {
  const dotnet: PluginManifestEntry[] = [];
  const python: PluginManifestEntry[] = [];

  entries.forEach((entry) => {
    if (!entry || !entry.id) {
      return;
    }
    const sources = sourcesById[entry.id] ?? [];
    const hasDotnet = sources.includes('dotnet');
    const hasPython = sources.includes('python');

    if (hasDotnet) {
      dotnet.push(entry);
    }
    if (hasPython) {
      python.push(entry);
    }

    if (!hasDotnet && !hasPython) {
      if (fallback === 'dotnet') {
        dotnet.push(entry);
      } else if (fallback === 'python') {
        python.push(entry);
      } else {
        dotnet.push(entry);
        python.push(entry);
      }
    }
  });

  return { dotnet, python };
}

export function getManifestSources(
  entryId: string | undefined,
  sourcesById: Record<string, ManifestSource[]>
) {
  if (!entryId) {
    return [];
  }
  return sourcesById[entryId] ?? [];
}
